import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    fetchBrands, adminCreateBrand, adminDeleteBrand,
    adminListUsers, adminCreateUser, adminDeleteUser,
} from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { AdminShell, Modal } from "./AdminShell";
import { MultiBrandQRModal } from "@/components/MultiBrandQRModal";
import { XReportModal } from "@/components/XReportModal";
import { LoyaltyMembersModal } from "@/components/LoyaltyMembersModal";
import { migrateLoyaltyPhones, adminDownloadBackupBlob, adminUpdateBrand } from "@/lib/api";
import { BrandOpenToggle } from "@/components/BrandOpenToggle";
import { CardReadersModal } from "@/components/CardReadersModal";

const AdminDashboard = () => {
    const { user } = useAdminAuth();
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewBrand, setShowNewBrand] = useState(false);
    const [showNewUser, setShowNewUser] = useState(false);
    const [showMultiQR, setShowMultiQR] = useState(false);
    const [showXReport, setShowXReport] = useState(false);
    const [showLoyalty, setShowLoyalty] = useState(false);
    const [backingUp, setBackingUp] = useState(false);
    const [showReaders, setShowReaders] = useState(false);
    const runBackup = async () => {
        setBackingUp(true);
        try {
            const blob = await adminDownloadBackupBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `order_backup_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.zip`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert("Backup failed: " + (e?.response?.status || e.message));
        } finally {
            setBackingUp(false);
        }
    };
    const [migrating, setMigrating] = useState(false);

    const runMigrateLoyaltyPhones = async () => {
        if (migrating) return;
        if (!window.confirm("This will canonicalise every loyalty member's phone number to +44 form so customers can sign in regardless of how they typed their number. Safe to re-run. Continue?")) {
            return;
        }
        setMigrating(true);
        try {
            const r = await migrateLoyaltyPhones();
            const collisionMsg = r.collisions?.length
                ? `\n\n⚠ ${r.collisions.length} collision(s) — needs manual merge:\n${r.collisions.map(c => `  • ${c.name} (${c.current_phone}) → ${c.collides_with} already has ${c.canonical_phone}`).join("\n")}`
                : "";
            alert(`✓ Migration done.\n\n${r.updated} member(s) updated\n${r.skipped} skipped (already canonical)${collisionMsg}`);
        } catch (e) {
            alert(e.response?.data?.detail || "Migration failed — try again.");
        } finally {
            setMigrating(false);
        }
    };

    const isMaster = user?.role === "master_admin";

    const refresh = async () => {
        const bs = await fetchBrands(false);
        setBrands(bs);
        if (isMaster) {
            try { setUsers(await adminListUsers()); } catch { /* ignore */ }
        }
        setLoading(false);
    };

    useEffect(() => {
        refresh();
        // brand_owner → push to their own brand
        if (user?.role === "brand_owner" && user.brand_id) {
            navigate(`/admin/brand/${user.brand_id}`, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return (
        <AdminShell
            eyebrow="Console"
            title={isMaster ? "All brands" : "Your brand"}
            right={
                isMaster && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={runMigrateLoyaltyPhones}
                            disabled={migrating}
                            className="btn-ghost"
                            data-testid="migrate-loyalty-phones-btn"
                            title="One-off: canonicalise every loyalty member's phone to +44 form. Safe to re-run."
                        >
                            {migrating ? "…" : "📞 Fix phones"}
                        </button>
                        <button onClick={() => setShowReaders(true)} className="btn-ghost" data-testid="card-readers-btn">
                            💳 Card readers
                        </button>
                        <button
                            onClick={runBackup}
                            disabled={backingUp}
                            className="btn-ghost"
                            data-testid="download-db-backup-btn"
                            title="Read-only snapshot of the whole database as a zip"
                        >
                            {backingUp ? "Backing up…" : "💾 DB Backup"}
                        </button>
                        <button
                            onClick={() => setShowLoyalty(true)}
                            className="btn-secondary"
                            data-testid="loyalty-members-btn"
                            title="View Naami Club members, points balances & tiers"
                        >
                            ⭐ Club Members
                        </button>
                        <button
                            onClick={() => setShowXReport(true)}
                            className="btn-secondary"
                            data-testid="x-report-btn"
                            title="End-of-shift snapshot — per-brand gross + total taken"
                        >
                            📊 X-Report
                        </button>
                        <button
                            onClick={() => setShowMultiQR(true)}
                            className="btn-secondary"
                            data-testid="multi-brand-qr-btn"
                            title="Print one QR code customers can scan to order across all brands"
                        >
                            ▦ Multi-brand QR
                        </button>
                        <button
                            onClick={() => setShowNewBrand(true)}
                            className="btn-primary"
                            data-testid="new-brand-btn"
                        >
                            + New brand
                        </button>
                    </div>
                )
            }
        >
            {loading ? (
                <p className="text-muted">Loading…</p>
            ) : (
                <>
                    <div
                        className="grid gap-4"
                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
                        data-testid="admin-brands-grid"
                    >
                        {brands.map((b) => (
                            <Link
                                key={b.id}
                                to={`/admin/brand/${b.id}`}
                                className="brand-card cursor-pointer"
                                style={{ "--brand-color": b.accent }}
                                data-testid={`admin-brand-${b.slug}`}
                            >
                                <div className="photo">
                                    {b.image_url && (
                                        <img src={b.image_url} alt={b.name} loading="lazy" />
                                    )}
                                    {!b.active && (
                                        <span className="opening-soon-stamp">Inactive</span>
                                    )}
                                    <div className="absolute top-3 right-3 z-[3]">
                                        <BrandOpenToggle
                                            brand={b}
                                            onToggle={async (closed, reopenAt) => {
                                                const u = await adminUpdateBrand(b.id, { temporarily_closed: closed, reopen_at: closed ? (reopenAt || "") : "" });
                                                setBrands((cur) => cur.map((x) => (x.id === b.id ? { ...x, temporarily_closed: u.temporarily_closed, reopen_at: u.reopen_at } : x)));
                                            }}
                                        />
                                    </div>
                                    <div className="photo-overlay" aria-hidden />
                                    <div className="stripe" aria-hidden />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-3.5">
                                        <div className="logo-box">{b.token}</div>
                                        <div className="min-w-0 flex-1">
                                            <h3
                                                className="font-display text-[22px] leading-tight truncate"
                                                style={{ color: b.accent }}
                                            >
                                                {b.name}
                                            </h3>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted truncate mt-0.5">
                                                {b.cuisine}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-4">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                            {b.items?.length || 0} items
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                                            Manage →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* User management — master only */}
                    {isMaster && (
                        <section className="mt-16">
                            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                                <div>
                                    <p className="eyebrow">// Users</p>
                                    <h2 className="font-display text-[32px] mt-2">Logins &amp; access</h2>
                                </div>
                                <button
                                    onClick={() => setShowNewUser(true)}
                                    className="btn-secondary"
                                    data-testid="new-user-btn"
                                >
                                    + Add brand owner
                                </button>
                            </div>
                            <div className="border border-[var(--border)] divide-y divide-[var(--border)]" data-testid="admin-users-list">
                                {users.map((u) => (
                                    <div key={u.id} className="p-4 flex items-center justify-between gap-3">
                                        <div>
                                            <div className="font-display text-[16px]">{u.name}</div>
                                            <div className="font-mono text-[11px] text-muted">{u.email}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
                                                {u.role === "master_admin" ? "Master" :
                                                    `Brand: ${brands.find(b => b.id === u.brand_id)?.name || "?"}`}
                                            </span>
                                            {u.id !== user.id && (
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm(`Delete ${u.email}?`)) {
                                                            await adminDeleteUser(u.id);
                                                            refresh();
                                                        }
                                                    }}
                                                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] hover:underline"
                                                    data-testid={`del-user-${u.id}`}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            {showNewBrand && (
                <NewBrandModal
                    onClose={() => setShowNewBrand(false)}
                    onCreated={() => { setShowNewBrand(false); refresh(); }}
                />
            )}
            {showNewUser && (
                <NewUserModal
                    brands={brands}
                    onClose={() => setShowNewUser(false)}
                    onCreated={() => { setShowNewUser(false); refresh(); }}
                />
            )}
            {showMultiQR && (
                <MultiBrandQRModal
                    baseUrl={window.location.origin}
                    onClose={() => setShowMultiQR(false)}
                />
            )}
            {showXReport && (
                <XReportModal onClose={() => setShowXReport(false)} />
            )}
            {showReaders && <CardReadersModal onClose={() => setShowReaders(false)} />}
            {showLoyalty && (
                <LoyaltyMembersModal onClose={() => setShowLoyalty(false)} />
            )}
        </AdminShell>
    );
};

const NewBrandModal = ({ onClose, onCreated }) => {
    const [form, setForm] = useState({
        name: "", slug: "", token: "", tagline: "", cuisine: "", accent: "#E8B339",
    });
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErr("");
        try {
            await adminCreateBrand(form);
            onCreated();
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <Modal title="New brand" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4">
                <Field label="Name"><input className="input" required value={form.name} onChange={setField("name")} data-testid="new-brand-name" /></Field>
                <Field label="Slug (URL friendly)"><input className="input" required value={form.slug} onChange={setField("slug")} placeholder="e.g. punjabi-pizza-pasta" data-testid="new-brand-slug" /></Field>
                <Field label="Token (2-3 chars)"><input className="input" required maxLength={4} value={form.token} onChange={setField("token")} placeholder="e.g. PP" data-testid="new-brand-token" /></Field>
                <Field label="Tagline"><input className="input" value={form.tagline} onChange={setField("tagline")} /></Field>
                <Field label="Cuisine"><input className="input" value={form.cuisine} onChange={setField("cuisine")} placeholder="e.g. Pizza" /></Field>
                <Field label="Accent colour">
                    <div className="flex gap-2 items-center">
                        <input type="color" className="h-12 w-16 bg-transparent border border-[var(--border)]" value={form.accent} onChange={setField("accent")} />
                        <input className="input flex-1" value={form.accent} onChange={setField("accent")} />
                    </div>
                </Field>
                {err && <p className="text-[#ff5246] text-sm font-mono">{err}</p>}
                <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="new-brand-submit">
                    {submitting ? "Creating…" : "Create brand"}
                </button>
            </form>
        </Modal>
    );
};

const NewUserModal = ({ brands, onClose, onCreated }) => {
    const [form, setForm] = useState({
        email: "", password: "", name: "", role: "brand_owner", brand_id: brands[0]?.id || "",
    });
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErr("");
        try {
            const body = { ...form };
            if (body.role === "master_admin") body.brand_id = null;
            await adminCreateUser(body);
            onCreated();
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };
    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <Modal title="Add login" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4">
                <Field label="Name"><input className="input" required value={form.name} onChange={setField("name")} /></Field>
                <Field label="Email"><input className="input" type="email" required value={form.email} onChange={setField("email")} data-testid="new-user-email" /></Field>
                <Field label="Password"><input className="input" type="text" required value={form.password} onChange={setField("password")} placeholder="They can change it later" data-testid="new-user-password" /></Field>
                <Field label="Role">
                    <select className="input" value={form.role} onChange={setField("role")}>
                        <option value="brand_owner">Brand owner</option>
                        <option value="master_admin">Master admin</option>
                    </select>
                </Field>
                {form.role === "brand_owner" && (
                    <Field label="Brand">
                        <select className="input" value={form.brand_id} onChange={setField("brand_id")} required>
                            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </Field>
                )}
                {err && <p className="text-[#ff5246] text-sm font-mono">{err}</p>}
                <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="new-user-submit">
                    {submitting ? "Creating…" : "Create login"}
                </button>
            </form>
        </Modal>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">{label}</span>
        {children}
    </label>
);

export default AdminDashboard;
